import { Badge } from '@/components/ui/badge';

interface ErrorEntry {
  code: number;
  name: string;
  meaning: string;
  exampleResponse: object;
}

const meta = { request_id: 'req-abc123', timestamp: '2026-10-15T09:13:00Z' };

// Mirrors the codes in internal/response/response.go. App endpoints add a
// granular code in error.details.code (see ErrorResponse in openapi.yaml).
const errorCodes: ErrorEntry[] = [
  {
    code: 400,
    name: 'INVALID_REQUEST / VALIDATION_ERROR / BAD_REQUEST',
    meaning:
      'Malformed JSON or bad path/query value (INVALID_REQUEST, BAD_REQUEST), or a missing/invalid field (VALIDATION_ERROR).',
    exampleResponse: {
      error: { code: 'VALIDATION_ERROR', message: 'name is required' },
      meta,
    },
  },
  {
    code: 401,
    name: 'UNAUTHORIZED',
    meaning: 'The X-API-Key header is missing, or the key is invalid, inactive or expired.',
    exampleResponse: {
      error: { code: 'UNAUTHORIZED', message: 'invalid API key' },
      meta,
    },
  },
  {
    code: 404,
    name: 'NOT_FOUND',
    meaning: 'The resource does not exist (or was soft-deleted). App endpoints add details.code, e.g. APP_NOT_FOUND.',
    exampleResponse: {
      error: { code: 'NOT_FOUND', message: 'App not found', details: { code: 'APP_NOT_FOUND' } },
      meta,
    },
  },
  {
    code: 409,
    name: 'CONFLICT',
    meaning: 'Duplicate name or a state conflict (e.g. NAME_EXISTS, NO_LOCKED_ENDPOINT, MIGRATION_VOLUME_MISMATCH).',
    exampleResponse: {
      error: { code: 'CONFLICT', message: 'App name already exists', details: { code: 'NAME_EXISTS' } },
      meta,
    },
  },
  {
    code: 500,
    name: 'INTERNAL_ERROR',
    meaning: 'Unexpected server or Portainer failure. details may carry the underlying error text.',
    exampleResponse: {
      error: { code: 'INTERNAL_ERROR', message: 'failed to fetch deployments', details: 'connection refused' },
      meta,
    },
  },
  {
    code: 501,
    name: 'NOT_IMPLEMENTED',
    meaning: 'The operation is not supported for this resource.',
    exampleResponse: {
      error: { code: 'NOT_IMPLEMENTED', message: 'not implemented' },
      meta,
    },
  },
  {
    code: 503,
    name: 'SERVICE_UNAVAILABLE',
    meaning: 'No endpoint can take the app right now (INSUFFICIENT_RESOURCES, MIGRATION_NO_CANDIDATE, MIGRATION_TIMEOUT).',
    exampleResponse: {
      error: {
        code: 'SERVICE_UNAVAILABLE',
        message: 'No available endpoint with sufficient resources',
        details: { code: 'INSUFFICIENT_RESOURCES' },
      },
      meta,
    },
  },
];

export function ErrorReference() {
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Error Reference</h3>
      <p className="text-sm text-muted-foreground">
        Errors return <code>{'{"error": {code, message, details}, "meta": {...}}'}</code>. Branch on{' '}
        <code>error.details.code</code> when present (app endpoints), otherwise on <code>error.code</code>.
      </p>
      <div className="rounded-lg border">
        <table className="w-full">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="px-4 py-3 text-left text-sm font-medium">Code</th>
              <th className="px-4 py-3 text-left text-sm font-medium">Name</th>
              <th className="px-4 py-3 text-left text-sm font-medium">Meaning</th>
              <th className="px-4 py-3 text-left text-sm font-medium">Example Response</th>
            </tr>
          </thead>
          <tbody>
            {errorCodes.map((error) => (
              <tr key={error.code} className="border-b last:border-b-0">
                <td className="px-4 py-3">
                  <Badge
                    variant={
                      error.code >= 500
                        ? 'destructive'
                        : error.code >= 400
                          ? 'secondary'
                          : 'default'
                    }
                  >
                    {error.code}
                  </Badge>
                </td>
                <td className="px-4 py-3 text-sm font-medium">{error.name}</td>
                <td className="px-4 py-3 text-sm text-muted-foreground">
                  {error.meaning}
                </td>
                <td className="px-4 py-3">
                  <pre className="text-xs bg-muted rounded p-2 overflow-x-auto max-w-xs">
                    <code>{JSON.stringify(error.exampleResponse, null, 2)}</code>
                  </pre>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}